import React from 'react';
import PropTypes from 'prop-types';

import {styled} from "@mui/material/styles";

import {ReactComponent as ArrowLeft} from "assets/img/arrow-left.svg";
import {ReactComponent as ArrowRight} from "assets/img/arrow-right.svg";
import {blue500, grey200, grey300, greyishBlue50, greyishBlue100, greyishBlue400} from "assets/jss/main";

ListingPagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func.isRequired,
    large: PropTypes.bool,
};

const largeProp = {shouldForwardProp: (prop) => prop !== 'large'};

const PaginationBar = styled('div', largeProp)(({large}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    padding: large ? 0 : '24px',
    backgroundColor: '#fff'
}));

const NavButton = styled('button', largeProp)(({large}) => ({
    width: large ? '40px' : '24px',
    height: large ? '40px' : '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'unset',
    border: 'none',
    borderRadius: large ? '8px' : '4px',
    backgroundColor: large ? grey300 : greyishBlue50,
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
        backgroundColor: greyishBlue100
    },
    '&:disabled': {
        cursor: 'default',
        opacity: large ? 1 : 0.5,
        backgroundColor: large ? grey200 : greyishBlue50
    },
    'svg': {
        width: '16px',
        height: '16px'
    },
    'svg path': {
        stroke: greyishBlue400
    }
}));

const PagesContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

const PageButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'large'
})(({active, large}) => ({
    minWidth: large ? '40px' : '24px',
    height: large ? '40px' : '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: large ? '8px' : '0 4px',
    border: 'none',
    borderRadius: large ? '8px' : '4px',
    backgroundColor: active ? blue500 : 'transparent',
    color: active ? '#fff' : greyishBlue400,
    fontSize: large ? '14px' : '12px',
    fontWeight: large ? 600 : 500,
    cursor: active ? 'default' : 'pointer',
    '&:hover': {
        backgroundColor: active ? blue500 : greyishBlue50
    }
}));

const Ellipsis = styled('span', largeProp)(({large}) => ({
    width: large ? '40px' : '24px',
    height: large ? '40px' : '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: greyishBlue400,
    fontSize: large ? '14px' : '12px',
    fontWeight: large ? 600 : 500
}));

function getPages(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);

    if (currentPage <= 3) {
        [2, 3, 4].forEach(page => pages.add(page));
    }

    if (currentPage >= totalPages - 2) {
        [totalPages - 3, totalPages - 2, totalPages - 1].forEach(page => pages.add(page));
    }

    const sortedPages = [...pages].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const pagesWithEllipsis = [];

    sortedPages.forEach((page, index) => {
        if (index > 0 && page - sortedPages[index - 1] > 1) {
            pagesWithEllipsis.push('...');
        }

        pagesWithEllipsis.push(page);
    });

    return pagesWithEllipsis;
}

function ListingPagination({currentPage, totalPages, onPageChange, large = false}) {
    if (!totalPages || totalPages <= 1) {
        return null;
    }

    return (
        <PaginationBar large={large}>
            <NavButton large={large} disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
                <ArrowLeft/>
            </NavButton>
            <PagesContainer>
                {getPages(currentPage, totalPages).map((page, index) => page === '...' ? (
                    <Ellipsis key={`ellipsis-${index}`} large={large}>...</Ellipsis>
                ) : (
                    <PageButton
                        key={page}
                        active={page === currentPage}
                        large={large}
                        onClick={() => page !== currentPage && onPageChange(page)}
                    >
                        {page}
                    </PageButton>
                ))}
            </PagesContainer>
            <NavButton large={large} disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
                <ArrowRight/>
            </NavButton>
        </PaginationBar>
    );
}

export default ListingPagination;
